import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import GratiPageWrapper from '../components/common/GratiPageWrapper';
import HandbookEditor from '../components/handbook/HandbookEditor';
import { ActionProps, useApp } from '../contextProviders/AppProvider';
import { useData } from '../contextProviders/DataProvider';
import useOrg from '../dataHooks/useOrg';
import { useHandbookMutators } from '../dataMutators/useHandbookMutators';
import DefaultObjects from '../types/DefaultObjects';
import Handbook from '../types/Handbook';
import { generateFirestoreId } from '../utils/utils';

export default function HandbookCreatePage(): ReactElement {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { setPageName } = useApp();
  const { canCreateHandbooks, userState } = useData();
  const { addHandbook } = useHandbookMutators();
  const { org, isOrgLoading, orgError } = useOrg(orgId ?? null);

  const [pageActions, setPageActions] = useState<ActionProps[] | null>(null);

  useEffect(() => {
    if (!orgId) {
      setPageName('Create Handbook');
    } else if (org) {
      setPageName(`Create ${org.name} Handbook`);
    }
  }, [setPageName, orgId, org]);

  // Pre-generate the document ID so we can use it for the logo path before saving
  const handbook = useMemo((): Handbook | null => {
    const docId = generateFirestoreId();
    return {
      ...DefaultObjects.Handbook,
      itemId: docId,
      creatorId: userState.userProfile?.itemId || '',
      creatorName: userState.userProfile?.name || '',
      orgId: org ? org.itemId : '',
      orgName: org ? org.name : '',
    };
  }, [org, userState.userProfile?.itemId, userState.userProfile?.name]);

  const handleCreate = useCallback((newHandbook: Handbook) => {
    addHandbook(newHandbook).then((addedHandbook) => {
      if (addedHandbook) {
        navigate(`/handbook/${addedHandbook.orgId}/${addedHandbook.itemId}`, {
          replace: true,
        });
      } else {
        console.error("Handbook not added: ", newHandbook);
      }
    });
  }, [addHandbook, navigate]);

  const handleActionsChange = useCallback((actions: ActionProps[] | null) => {
    setPageActions(actions);
  }, []);

  const validateUser = () => {
    return canCreateHandbooks(orgId) ?? false;
  };

  return (
    <GratiPageWrapper
      validateUser={validateUser}
      isContentLoading={isOrgLoading}
      contentError={orgError}
      pageActions={pageActions}
    >
      {handbook && (
        <HandbookEditor
          handbook={handbook}
          isNew
          handleCreate={handleCreate}
          onActionsChange={handleActionsChange}
        />
      )}
    </GratiPageWrapper>
  );
}
